import { useCallback } from 'react';
import { useInfiniteMeetverseQuestions } from '@hooks/useInfiniteMeetverseQuestions';
import { MEETVERSE_FEED_STALE_TIME_MS } from '@lib/feed/constants';
import { useAuth } from '@providers';
import type { UseHomeFeedReturn } from '../types';

/**
 * Hook for the Meetverse tab feed.
 * Simplified from dual-query (meetverse + events) to meetverse-only.
 * Event filtering was removed with the FilterBar.
 *
 * No `refetchInterval` is set on the infinite query: live freshness is delivered
 * via SSE invalidation (when connected) and via the "new posts" pill (which uses
 * its own cheap detector poll when SSE is down — see useNewMeetversePostsIndicator).
 * Refetching every page on a timer was wasteful and produced cursor-offset drift.
 */
export function useHomeFeed(enabled = true): UseHomeFeedReturn {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();

  const isQueryEnabled = enabled && !isAuthLoading;

  const {
    questions,
    fetchNextPage: fetchNextPageFn,
    hasNextPage,
    isFetchingNextPage,
    isLoading: questionsLoading,
    isError,
    error,
    refresh,
  } = useInfiniteMeetverseQuestions({
    enabled: isQueryEnabled,
    // AIDEV-NOTE: TECH-305 — this staleTime is load-bearing. After creating a post we prepend
    // it into this Redis-served feed via setQueriesData and intentionally do NOT force-refetch
    // (see useCreateQuestionMutation onSuccess). Staying fresh across the navigate-back mount is
    // what stops a refetch from clobbering the new post before the backend fan-out worker
    // populates Redis — and because refetchOnWindowFocus below is also gated by this same
    // freshness, a focus event inside the window won't refetch either. Don't lower below
    // worst-case fan-out latency, and don't add a refetchInterval here, without restoring
    // explicit post-create reconciliation. Tune it via MEETVERSE_FEED_STALE_TIME_MS so the
    // just-posted splice window (derived from the same constant) moves with it.
    staleTime: MEETVERSE_FEED_STALE_TIME_MS,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: isQueryEnabled,
  });

  const isLoading = isAuthLoading || questionsLoading;

  const fetchNextPage = useCallback(() => {
    void fetchNextPageFn();
  }, [fetchNextPageFn]);

  return {
    questions,
    isLoading,
    isLoadingMore: isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isEmpty: !isLoading && questions.length === 0,
    feedType: isAuthenticated ? 'meetverse' : 'public',
    isError,
    error: error ?? null,
    refresh,
  };
}
